import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { startPurchase } from "@/lib/api";
import { TcsAgreement } from "@/components/TcsAgreement";
import api, { AED, deliveryFeeForBox, useDeliveryFees, fmtDateTime } from "@/lib/api";
import { listingStatusLabel } from "@/lib/statusLabels";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import PaymentTrust from "@/components/PaymentTrust";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Heart, MapPin, MoreVertical, Send, ShieldCheck, Eye, EyeOff, Package, CheckCircle2, Check, Flag, ChevronLeft, ChevronRight, X, Pencil, Trash2, Share2, Maximize2, RefreshCw } from "lucide-react";
import { RichText } from "@/components/RichText";

const _imgSrc = (s) => (!s ? "" : s.startsWith("http") || s.startsWith("data:") || s.startsWith("/") ? s : `data:image/jpeg;base64,${s}`);

export function ConfirmPurchaseModal({ open, onOpenChange, listing, user, feeConfig, price, negId, isFree }) {
  const [viewed, setViewed] = useState({});
  const [photoAck, setPhotoAck] = useState(false);
  const [tcs, setTcs] = useState(false);
  const [preview, setPreview] = useState(null);
  const [quote, setQuote] = useState(null);
  const [quoteErr, setQuoteErr] = useState("");
  useEffect(() => { if (!open) { setViewed({}); setPhotoAck(false); setTcs(false); setPreview(null); setQuote(null); setQuoteErr(""); } }, [open]);
  useEffect(() => {
    if (!open || !listing) return;
    api.get("/payments/quote", { params: { listing_id: listing.id, ...(negId ? { negotiation_id: negId } : {}) } })
      .then(({ data }) => setQuote(data)).catch((e) => setQuoteErr(e.response?.data?.detail || "Could not load the price"));
  }, [open, listing?.id, negId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!listing) return null;
  const photos = listing.seller_photos || [];
  // Every line comes from the server quote — the same calculation Stripe is charged with.
  const itemPrice = quote?.item;
  const fee = quote?.delivery;
  const owed = quote?.balance || 0;
  const total = quote?.total;
  const hasPhotos = photos.length > 0;
  const gateMet = !hasPhotos || Object.keys(viewed).length > 0;
  const view = (i) => { setViewed((v) => (v[i] ? v : { ...v, [i]: new Date().toISOString() })); setPreview(i); };
  const go = () => {
    onOpenChange(false);
    startPurchase(listing.id, negId || null, true, { ack: true, ack_at: new Date().toISOString(), viewed: Object.entries(viewed).map(([index, at]) => ({ index: Number(index), at })) });
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl" data-testid="tcs-modal">
        <DialogHeader><DialogTitle>Confirm your purchase</DialogTitle><DialogDescription className="sr-only">Review the item, price, delivery fee, total and delivery address, view the seller's photos and accept the terms before continuing to checkout.</DialogDescription></DialogHeader>
        <div className="rounded-xl bg-slate-50 p-3 text-sm" data-testid="tcs-modal-item">
          <p className="font-heading font-semibold text-slate-900" data-testid="tcs-modal-title">{listing.title}</p>
          <div className="mt-2 space-y-1 text-slate-600">
            <div className="flex justify-between"><span>{isFree ? "Item (free)" : "Item"}</span><span data-testid="tcs-modal-price">{quote ? AED(itemPrice) : "…"}</span></div>
            <div className="flex justify-between"><span>Delivery</span><span data-testid="tcs-modal-delivery">{quote ? AED(fee) : "…"}</span></div>
            {owed > 0 && <div className="flex justify-between text-red-700" data-testid="tcs-modal-balance"><span>Outstanding balance (failed delivery)</span><span>{AED(owed)}</span></div>}
            <div className="flex justify-between font-semibold text-slate-900"><span>Total</span><span data-testid="tcs-modal-total">{quote ? AED(total) : "…"}</span></div>
            {quoteErr && <p className="text-xs text-red-600" data-testid="tcs-modal-quote-error">{quoteErr}</p>}
          </div>
          <div className="mt-2 flex items-center gap-1 border-t border-slate-200 pt-2 text-xs text-slate-500">
            <MapPin className="h-3 w-3 shrink-0" /><span className="truncate" data-testid="tcs-modal-address">{user?.area ? `Deliver to ${user.area}` : "No delivery address set"}</span>
            <a href="/profile" data-testid="tcs-modal-change-address" className="ml-auto shrink-0 font-medium text-primary hover:underline">Change</a>
          </div>
        </div>
        <div data-testid="checkout-seller-photos">
          <p className="mb-1.5 text-xs font-semibold text-slate-700">Seller's photos — the actual item</p>
          {preview !== null && photos[preview] && <img src={_imgSrc(photos[preview])} alt="" data-testid="tcs-photo-preview" className="mb-2 max-h-48 w-full rounded-xl bg-slate-100 object-contain" />}
          {hasPhotos ? (
            <div className="flex flex-wrap gap-2">
              {photos.map((p, i) => (
                <button key={i} type="button" data-testid={`checkout-seller-photo-${i}`} onMouseEnter={() => view(i)} onClick={() => view(i)} className="relative h-14 w-14 shrink-0 cursor-pointer overflow-hidden rounded-lg border border-slate-200">
                  <img src={_imgSrc(p)} alt={`Seller ${i}`} className="h-full w-full object-cover" />
                  {viewed[i] && <span className="absolute right-0.5 top-0.5 rounded-full bg-emerald-600 p-0.5 text-white"><Check className="h-3 w-3" /></span>}
                </button>
              ))}
            </div>
          ) : <p className="text-xs text-slate-400" data-testid="checkout-no-photos">This older listing has no seller photos. Buyer Protection still applies.</p>}
          {hasPhotos && Object.keys(viewed).length === 0 && <p className="mt-1.5 text-xs font-medium text-amber-700" data-testid="checkout-photo-hint">Hover or tap a photo to view the actual item</p>}
        </div>
        <label className="flex cursor-pointer items-start gap-2.5 text-sm text-slate-700">
          <input type="checkbox" data-testid="checkout-photo-ack" checked={photoAck} disabled={!gateMet} onChange={(e) => setPhotoAck(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 disabled:opacity-40" />
          <span>I've viewed the seller's real photos of the item.</span>
        </label>
        <TcsAgreement checked={tcs} onChange={setTcs} testid="tcs-modal-agree" />
        <PaymentTrust className="mt-1" testid="modal-payment-trust" />
        <DialogFooter className="mt-2 flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
          <button type="button" data-testid="tcs-modal-cancel" onClick={() => onOpenChange(false)} className="text-sm font-medium text-slate-500 underline hover:text-slate-700">Cancel</button>
          <Button data-testid="tcs-modal-continue" disabled={!photoAck || !tcs || !quote} onClick={go} className="rounded-full bg-primary font-semibold hover:bg-primary/90 disabled:opacity-50">Continue to checkout</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ListingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const autoOffer = sp.get("offer") === "1";
  const { user } = useAuth();
  const feeConfig = useDeliveryFees();
  const [listing, setListing] = useState(null);
  const [err, setErr] = useState(null);
  const [saved, setSaved] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("Fake/Counterfeit item");
  const [reportDetails, setReportDetails] = useState("");
  const [lightbox, setLightbox] = useState(null);
  const [adminRemove, setAdminRemove] = useState(false);
  const [regenCover, setRegenCover] = useState(false);
  const [buyModal, setBuyModal] = useState(false);
  const [buyNeg, setBuyNeg] = useState(null);
  const [buyPrice, setBuyPrice] = useState(null);
  const [held, setHeld] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/listings/${id}`);
      setListing(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Not found");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Is this exact listing currently reserved for the signed-in buyer? (same source as the hold bar)
  useEffect(() => {
    if (!listing || !user || user.is_admin || listing.seller_id === user.id) { setHeld(null); return; }
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/pending");
        const p = (data.purchases || []).find((x) => x.listing_id === listing.id) || null;
        if (active) setHeld(p);
      } catch { if (active) setHeld(null); }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listing?.id, listing?.status, user?.id]);

  // Part 1: dynamic Open Graph tags so the shared link unfurls with image/title/price.
  useEffect(() => {
    if (!listing) return;
    const url = typeof window !== "undefined" ? window.location.href : "";
    const img = imgSrc(listing.ai_thumbnail || (listing.seller_photos || [])[0] || "");
    const tags = {
      "og:title": listing.title || "SellStuff listing",
      "og:description": `${AED(listing.listing_price)} · Includes delivery · SellStuff`,
      "og:image": img,
      "og:url": url,
      "og:type": "product",
    };
    const prevTitle = document.title;
    document.title = `${listing.title || "Listing"} · SellStuff`;
    const created = [];
    Object.entries(tags).forEach(([property, content]) => {
      let el = document.head.querySelector(`meta[property="${property}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("property", property); document.head.appendChild(el); created.push(el); }
      el.setAttribute("content", content);
    });
    return () => { document.title = prevTitle; created.forEach((el) => el.remove()); };
  }, [listing]);

  const save = async () => {
    if (!user) return navigate("/login");
    try {
      if (saved) { await api.delete(`/listings/${id}/save`); setSaved(false); toast("Removed from watchlist"); }
      else { await api.post(`/listings/${id}/save`); setSaved(true); toast.success("Saved to watchlist"); }
    } catch { toast.error("Could not update watchlist"); }
  };

  const regenerateCover = async () => {
    if (regenCover) return;
    setRegenCover(true);
    try {
      const { data } = await api.post("/listing-chat/regenerate-thumbnail", { listing_id: listing.id });
      if (data.ai_thumbnail) { setListing((l) => ({ ...l, ai_thumbnail: data.ai_thumbnail })); toast.success("Cover updated"); }
      else toast.error("Couldn't generate a new image — please try again");
    } catch { toast.error("Could not regenerate cover"); } finally { setRegenCover(false); }
  };

  const adminToggle = async () => {
    const active = listing.status === "active";
    try { await api.post(`/admin/listings/${id}/${active ? "hide" : "reinstate"}`); toast.success(active ? "Listing hidden from browse" : "Listing reinstated"); load(); }
    catch { toast.error("Action failed"); }
  };
  const adminDelete = async () => {
    try { await api.post(`/admin/listings/${id}/remove`); toast.success("Listing removed from marketplace"); navigate("/admin"); }
    catch { toast.error("Could not remove"); }
  };

  const submitReport = async () => {
    try {
      await api.post(`/listings/${id}/report`, { reason: reportReason, details: reportDetails });
      toast.success("Reported. Our team will review it.");
      setReportOpen(false);
    } catch { toast.error("Could not report"); }
  };

  if (err) return <div className="mx-auto max-w-2xl px-4 py-20 text-center" data-testid="listing-locked"><Package className="mx-auto mb-4 h-12 w-12 text-slate-300" /><p className="text-slate-600">{err}</p><Button onClick={() => navigate("/")} className="mt-4 rounded-full">Back to browse</Button></div>;
  if (!listing || !feeConfig) return <div className="py-20 text-center text-slate-400">Loading…</div>;

  const isOwner = user && listing.seller_id === user.id;
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const photos = listing.seller_photos || [];
  const coverSrc = listing.ai_thumbnail || photos[0] || "";
  const isCoverFallback = !listing.ai_thumbnail && !!photos[0];
  const allImages = [listing.ai_thumbnail, ...photos].filter(Boolean).map(imgSrc);
  const thumbOffset = listing.ai_thumbnail ? 1 : 0;

  // Key specs include condition; prepend it if the stored specs don't already carry it.
  const specItems = (() => {
    const arr = (listing.specs || []).filter((s) => (s || "").trim());
    const hasCond = arr.some((s) => /^\s*condition\s*:/i.test(s));
    return !hasCond && listing.condition ? [`Condition: ${listing.condition}`, ...arr] : arr;
  })();

  const KeySpecsBlock = ({ suffix = "" }) => specItems.length === 0 ? null : (
    <div data-testid={`key-specs${suffix}`}>
      <h3 className="font-heading font-semibold text-slate-900">Key specs</h3>
      <ul className="mt-2 space-y-1.5">
        {specItems.map((s, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-slate-600"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" /> {s}</li>
        ))}
      </ul>
    </div>
  );

  const DescriptionBlock = ({ suffix = "" }) => (
    <div>
      <h3 className="font-heading font-semibold text-slate-900">Description</h3>
      <p className="mt-1 whitespace-pre-line text-sm text-slate-600" data-testid={`detail-description${suffix}`}>{listing.description}</p>
    </div>
  );

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
      <Lightbox images={allImages} index={lightbox} setIndex={setLightbox} onClose={() => setLightbox(null)} />
      {user?.is_admin && (
        <div data-testid="admin-listing-toolbar" className="mb-6 flex flex-wrap items-center gap-2 rounded-2xl border border-amber-200 bg-amber-50 p-3">
          <Badge className="rounded-full bg-amber-500 text-white hover:bg-amber-500">Admin</Badge>
          <span className="mr-auto text-sm text-amber-800">Status: <b>{listingStatusLabel(listing.status)}</b></span>
          <Button size="sm" variant="outline" className="rounded-full" data-testid="admin-edit-listing" onClick={() => navigate(`/listing/${id}/edit`)}><Pencil className="mr-1 h-4 w-4" /> Edit Listing</Button>
          {listing.status === "pending_review" ? (<>
            <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="admin-detail-approve" onClick={async () => { try { await api.post(`/admin/listings/${id}/approve`); toast.success("Listing approved — seller emailed"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Could not approve"); } }}>Approve</Button>
            <Button size="sm" variant="outline" className="rounded-full border-red-300 text-red-700" data-testid="admin-detail-decline" onClick={async () => { const reason = (window.prompt("Reason for declining (sent to the seller):") || "").trim(); if (!reason) return; try { await api.post(`/admin/listings/${id}/decline`, { reason }); toast.success("Listing declined — seller emailed"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Could not decline"); } }}>Decline</Button>
          </>) : <Button size="sm" variant="outline" className="rounded-full" data-testid="admin-hide-listing" onClick={adminToggle}>{listing.status === "active" ? <><EyeOff className="mr-1 h-4 w-4" /> Hide Listing</> : <><Eye className="mr-1 h-4 w-4" /> Reinstate Listing</>}</Button>}
          <Button size="sm" variant="outline" className="rounded-full border-red-200 text-red-600 hover:bg-red-50" data-testid="admin-remove-listing" onClick={() => setAdminRemove(true)}><Trash2 className="mr-1 h-4 w-4" /> Remove from Marketplace</Button>
        </div>
      )}
      {user?.is_admin && (
        <div data-testid="admin-pricing-breakdown" className="mb-6 rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <h3 className="mb-3 font-heading font-semibold text-slate-900">Pricing breakdown (admin only)</h3>
          <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
            <Fact label="SellStuff listing price (public)" value={AED(listing.listing_price)} />
            <Fact label="Seller desired payout" value={AED(listing.seller_desired_payout)} />
            <Fact label="Seller floor payout" value={AED(listing.seller_floor_payout)} />
            <Fact label="Floor listing price (Max min)" value={AED(listing.floor_listing_price)} />
            <Fact label="Raw calculated price" value={AED(listing.raw_listing_price)} />
            <Fact label="Seller price approved" value={listing.seller_price_approved ? "Yes" : "No"} />
            <Fact label="Approved at" value={listing.seller_price_approved_at ? fmtDateTime(listing.seller_price_approved_at) : "—"} />
          </div>
          {listing.seller_price_approved_breakdown && (
            <details className="mt-3">
              <summary className="cursor-pointer text-sm font-medium text-primary">Approval breakdown (JSON)</summary>
              <pre data-testid="admin-approval-json" className="mt-2 overflow-x-auto rounded-lg bg-slate-900 p-3 text-xs text-slate-100">{JSON.stringify(listing.seller_price_approved_breakdown, null, 2)}</pre>
            </details>
          )}
        </div>
      )}
      {user?.is_admin && (
        <div data-testid="admin-seller-confirmations" className="mb-6 rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <h3 className="mb-3 font-heading font-semibold text-slate-900">Seller confirmations</h3>
          <div className="space-y-1.5 text-sm text-slate-700">
            <ConfRow label="Collection terms agreed" at={listing.seller_size_confirmed_at} ok={listing.seller_size_confirmed} />
            <ConfRow label="Genuine & not counterfeit" at={listing.seller_counterfeit_confirmed_at} ok={listing.seller_counterfeit_confirmed} />
            <ConfRow label="Description accurate" at={listing.desc_accurate_at} ok={listing.desc_accurate} />
            <div className="flex items-center justify-between" data-testid="admin-conf-terms-version">
              <span className="text-slate-500">Legal version accepted</span>
              <span className="font-medium text-slate-800">{listing.terms_version_accepted ? `v${listing.terms_version_accepted}` : "—"}</span>
            </div>
          </div>
        </div>
      )}
      <Dialog open={adminRemove} onOpenChange={setAdminRemove}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>Remove from marketplace?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">This removes the listing from browse and search and notifies the seller. The record is kept and can be reinstated at any time.</p>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setAdminRemove(false)}>Cancel</Button>
            <Button className="rounded-full bg-red-600 hover:bg-red-700" data-testid="admin-remove-confirm" onClick={adminDelete}>Remove from Marketplace</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <div className="grid gap-8 lg:grid-cols-2 lg:items-start">
        {/* Left: images (+ desktop key specs & description) */}
        <div className="min-w-0">
          <button type="button" onClick={() => setLightbox(0)} data-testid="main-image" className="relative block aspect-square w-full cursor-zoom-in overflow-hidden rounded-2xl border border-slate-200 bg-slate-100">
            {coverSrc && <img src={imgSrc(coverSrc)} alt={listing.title} className="h-full w-full object-cover" />}
            {!isCoverFallback && coverSrc && <span className="absolute left-2 top-2 rounded bg-black/60 px-2 py-0.5 text-[10px] text-white" data-testid="ai-enhanced-label">AI-enhanced image</span>}
            {isCoverFallback && coverSrc && <span className="absolute bottom-2 left-2 rounded bg-black/60 px-2 py-0.5 text-[10px] text-white" data-testid="cover-fallback-label">Seller's photo</span>}
            {regenCover && <span className="absolute inset-0 flex items-center justify-center bg-white/60" data-testid="cover-regenerating"><RefreshCw className="h-8 w-8 animate-spin text-primary" /></span>}
          </button>
          {isOwner && coverSrc && (
            <button type="button" data-testid="regenerate-cover-btn" onClick={regenerateCover} disabled={regenCover} className="mt-2 flex items-center gap-1 text-xs font-medium text-primary hover:underline disabled:opacity-50">
              <RefreshCw className={`h-3.5 w-3.5 ${regenCover ? "animate-spin" : ""}`} /> {regenCover ? "Generating…" : "Regenerate cover"}
            </button>
          )}
          {photos.length > 0 && (
            <div className="mt-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Seller's photos</p>
              <div className="grid grid-cols-4 gap-3">
                {photos.map((p, i) => (
                  <button key={i} type="button" onClick={() => setLightbox(thumbOffset + i)} data-testid={`seller-photo-${i}`} className="group relative block aspect-square cursor-zoom-in overflow-hidden rounded-xl border border-slate-200">
                    <img src={imgSrc(p)} alt={`Seller ${i}`} className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105" />
                    <span className="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-all group-hover:bg-black/20 group-hover:opacity-100"><Maximize2 className="h-5 w-5 text-white" /></span>
                    <span className="absolute bottom-1 left-1 rounded bg-black/60 px-1 py-0.5 text-[8px] text-white">Seller's photo</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Desktop-only: key specs then description */}
          <div className="mt-8 hidden space-y-6 lg:block" data-testid="details-desktop">
            <KeySpecsBlock />
            <DescriptionBlock />
            <IncludesChips listing={listing} />
          </div>
        </div>

        {/* Right: info + Max (sticky on desktop) */}
        <div className="min-w-0 lg:sticky lg:top-20 lg:self-start">
          {isOwner && (
            <div className="mb-5 rounded-2xl border border-slate-200 bg-slate-50 p-4" data-testid="own-listing-notice">
              <p className="font-medium text-slate-900">{listing.status === "sold" ? "This listing has sold" : "This is your listing"}</p>
              <p className="mt-1 text-sm text-slate-500">{listing.status === "sold" ? "This item has been sold and is no longer available to buyers." : "Buyers negotiate with Max on your behalf — you'll be notified when it sells."}</p>
              {listing.status === "sold" ? (
            <p className="mt-3 rounded-lg bg-slate-100 px-3 py-2 text-sm font-medium text-slate-600" data-testid="sold-readonly-notice">This listing has sold and can no longer be edited.</p>
          ) : (
            <Button data-testid="manage-listing-btn" variant="outline" onClick={() => navigate(`/listing/${listing.id}/edit`)} className="mt-3 rounded-full border-2 border-primary bg-white font-semibold text-primary hover:bg-primary/5">Manage Listing</Button>
          )}
            </div>
          )}

          <div className="flex items-start justify-between gap-2">
            <div className="flex flex-wrap gap-2">
              {listing.status === "sold" && <Badge className="rounded-full bg-slate-900 text-white hover:bg-slate-900" data-testid="detail-sold-badge">Sold</Badge>}
              <Badge className="rounded-full bg-blue-100 text-blue-700 hover:bg-blue-100" data-testid="detail-condition">{listing.condition}</Badge>
              <Badge variant="outline" className="rounded-full" data-testid="detail-category">{listing.category}{listing.subcategory ? ` › ${listing.subcategory}` : ""}</Badge>
            </div>
            {!isOwner && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild><button data-testid="listing-menu" className="rounded-full p-2 hover:bg-slate-100" aria-label="More"><MoreVertical className="h-5 w-5 text-slate-500" /></button></DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => user ? setReportOpen(true) : navigate("/login")} data-testid="report-open"><Flag className="mr-2 h-4 w-4" /> Report listing</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          <h1 className="mt-3 font-heading text-2xl font-bold leading-tight text-slate-900" data-testid="detail-title">{listing.title}</h1>
          <p className="mt-1 text-sm text-slate-500" data-testid="detail-location">Sold by {listing.seller_display_name || "a SellStuff seller"} · {listing.seller_location || "Dubai, UAE"}</p>
          {user?.is_admin && listing.listing_type === "free" && (
            <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700" data-testid="admin-free-details">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Free Item Details</p>
              <p>Listing type: <b>Free</b></p>
              <p>Max estimated value: <b>{AED(listing.estimated_value || 0)}</b></p>
              <p>Delivery fee charged to buyer: <b>{AED(deliveryFeeForBox(listing.preferred_box_size, feeConfig))}</b></p>
              <p>Processing fee: <b>AED 0.00</b> (absorbed by SellStuff)</p>
              <p>Seller payout: <b>AED 0.00</b></p>
            </div>
          )}

          <div className="mt-4 flex items-baseline gap-3">
            {listing.listing_type === "free"
              ? <span className="font-heading text-4xl font-extrabold" style={{ color: "#3B63E0" }} data-testid="detail-price-free">FREE</span>
              : <span className="font-heading text-4xl font-extrabold text-slate-900" data-testid="detail-price">{AED(listing.listing_price)}</span>}
          </div>

          <p className="mt-2 flex items-center gap-1 text-sm text-slate-500" data-testid="detail-delivery">
            <Check className="h-4 w-4 text-emerald-500" /> {listing.listing_type === "free" ? "Delivered directly to your door" : `Includes delivery anywhere in ${listing.seller_emirate || "the UAE"}`}
          </p>

          {(() => {
            const paid = listing.rrp_price || 0;
            const saving = paid - listing.listing_price;
            if (paid <= listing.listing_price || saving <= 50) return null;
            return (
              <p className="mt-3 text-sm text-slate-600" data-testid="savings-line">
                Purchased for <span className="text-slate-400 line-through" data-testid="savings-paid">{AED(paid)}</span> · <span className="font-semibold text-emerald-600" data-testid="savings-amount">Save {AED(saving)}</span>
              </p>
            );
          })()}

          {!isOwner && listing.status === "sold" && (
            <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600" data-testid="buyer-sold-notice">This item has been sold and is no longer available.</div>
          )}

          {listing.status === "declined" && (
            <div className="mt-5 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800" data-testid="listing-declined-notice"><b>Declined</b>{listing.decline_reason ? ` — ${listing.decline_reason}` : ""}</div>
          )}

          {!isOwner && listing.status === "pending_review" && (
            <div className="mt-5 rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm font-medium text-blue-800" data-testid="buyer-pending-review-notice">Pending review — not visible to buyers</div>
          )}

          {!isOwner && listing.status !== "sold" && listing.status !== "pending_review" && listing.status !== "declined" && (
            <>
              {listing.listing_type === "free" ? (
                <FreeClaim listing={listing} user={user} navigate={navigate} coverSrc={imgSrc(coverSrc)} />
              ) : (
              <>
              <div className="mt-5 flex gap-3">
                {held ? (
                  <div className="flex-1">
                    <p className="mb-1 flex items-center gap-1 text-xs font-semibold text-primary" data-testid="reserved-for-you"><ShieldCheck className="h-3.5 w-3.5" /> Reserved for you</p>
                    <Button data-testid="complete-purchase-btn" onClick={() => { setBuyNeg(held.negotiation_id || null); setBuyPrice(held.agreed_price ?? null); setBuyModal(true); }} className="w-full rounded-full bg-primary font-semibold hover:bg-primary/90">Complete Purchase</Button>
                  </div>
                ) : (
                  <Button data-testid="buy-now-btn" onClick={() => user ? (setBuyNeg(null), setBuyModal(true)) : navigate("/login")} className="flex-1 rounded-full bg-primary font-semibold hover:bg-primary/90">Buy Now</Button>
                )}
                <Button variant="outline" onClick={save} data-testid="save-btn" className="rounded-full" aria-label="Save"><Heart className={`h-4 w-4 ${saved ? "fill-red-500 text-red-500" : ""}`} /></Button>
                <ShareButton url={shareUrl} title={listing.title} />
              </div>
              <PaymentTrust className="mt-2" testid="listing-payment-trust" />

              {user ? <MaxChat listing={listing} autoOpen={autoOffer} onBuy={(nid, price) => { setBuyNeg(nid); setBuyPrice(price ?? null); setBuyModal(true); }} /> : (
                <div className="mt-3">
                  <Button variant="outline" onClick={() => navigate("/login")} className="w-full rounded-full">Log in to make an offer with Max</Button>
                  <p data-testid="offer-explainer" className="mt-1.5 text-xs text-slate-500">Max negotiates on the seller's behalf.</p>
                </div>
              )}
              </>
              )}

              <p data-testid="buyer-confidence" className="mt-3 text-sm text-slate-500">{listing.listing_type === "free" ? "This is a free item — delivery fee only, no returns apply." : "🛡️ Buy with confidence — 24 hours after delivery to raise a concern"}</p>
            </>
          )}

          {/* Mobile-only: key specs then description */}
          <div className="mt-6 space-y-6 lg:hidden" data-testid="details-mobile">
            <KeySpecsBlock suffix="-m" />
            <DescriptionBlock suffix="-m" />
            <IncludesChips listing={listing} suffix="-m" />
          </div>
        </div>
      </div>

      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>Report this listing</DialogTitle></DialogHeader>
          <Select value={reportReason} onValueChange={setReportReason}>
            <SelectTrigger data-testid="report-reason"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["Fake/Counterfeit item", "Prohibited item", "Spam", "Inaccurate description", "Item already sold", "Suspicious or fraudulent seller", "Other"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input placeholder="Additional details (optional)" value={reportDetails} onChange={(e) => setReportDetails(e.target.value)} data-testid="report-details" />
          <DialogFooter><Button onClick={submitReport} data-testid="report-submit" className="rounded-full bg-primary">Submit report</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmPurchaseModal open={buyModal} onOpenChange={setBuyModal} listing={listing} user={user} feeConfig={feeConfig} price={buyNeg ? buyPrice : undefined} negId={buyNeg} />
    </div>
  );
}

const Fact = ({ label, value }) => (
  <div className="rounded-xl border border-slate-200 p-3"><p className="text-xs text-slate-400">{label}</p><p className="font-medium text-slate-800">{value}</p></div>
);

const ConfRow = ({ label, at, ok }) => (
  <div className="flex items-center justify-between" data-testid={`admin-conf-${label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}>
    <span className="text-slate-500">{label}</span>
    <span className={at || ok ? "font-medium text-slate-800" : "text-slate-400"}>{at ? fmtDateTime(at) : ok ? "Confirmed" : "Not recorded"}</span>
  </div>
);

const capitalise = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

function IncludesChips({ listing, suffix = "" }) {
  const raw = [];
  if (listing.has_original_box) raw.push("Original box");
  if (listing.has_receipt) raw.push("Receipt");
  (listing.accessories || []).forEach((a) => { const t = (a || "").trim(); if (t) raw.push(capitalise(t)); });
  const seen = new Set();
  const chips = [];
  for (const it of raw) { const key = it.toLowerCase(); if (!seen.has(key)) { seen.add(key); chips.push(it); } }
  // Seller tapped "Nothing to add" (no includes and no note) → omit the section entirely.
  if (!chips.length && !listing.has_seller_note) return null;
  return (
    <div data-testid={`includes-section${suffix}`}>
      <h3 className="font-heading font-semibold text-slate-900">What's included</h3>
      {chips.length ? (
        <div className="mt-2 flex flex-wrap gap-2" data-testid={`includes-chips${suffix}`}>
          {chips.map((c, i) => (
            <Badge key={i} variant="outline" data-testid={`include-chip${suffix}-${i}`} className="rounded-full bg-slate-50 text-slate-700">{c}</Badge>
          ))}
        </div>
      ) : (
        <p className="mt-1 text-sm text-slate-500" data-testid={`no-extras${suffix}`}>No extras included</p>
      )}
    </div>
  );
}

function ShareButton({ url, title }) {
  const canNativeShare = typeof navigator !== "undefined" && !!navigator.share && /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent || "");
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    catch {
      try { const ta = document.createElement("textarea"); ta.value = url; ta.style.position = "fixed"; ta.style.opacity = "0"; document.body.appendChild(ta); ta.focus(); ta.select(); document.execCommand("copy"); document.body.removeChild(ta); toast.success("Link copied"); }
      catch { toast.error("Could not copy link"); }
    }
  };
  const shareWhatsApp = () => window.open(`https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}`, "_blank");
  const nativeShare = async () => { try { await navigator.share({ title, url }); } catch { /* cancelled */ } };
  if (canNativeShare) {
    return <Button variant="outline" onClick={nativeShare} data-testid="share-btn" className="rounded-full" aria-label="Share"><Share2 className="h-4 w-4" /></Button>;
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild><Button variant="outline" data-testid="share-btn" className="rounded-full" aria-label="Share"><Share2 className="h-4 w-4" /></Button></DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem data-testid="share-copy" onClick={copyLink}>Copy link</DropdownMenuItem>
        <DropdownMenuItem data-testid="share-whatsapp" onClick={shareWhatsApp}>Share on WhatsApp</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const imgSrc = (s) => (!s ? "" : s.startsWith("http") || s.startsWith("data:") || s.startsWith("/") ? s : `data:image/jpeg;base64,${s}`);

function Lightbox({ images, index, setIndex, onClose }) {
  const open = index !== null && images.length > 0;
  const safe = open ? Math.min(index, images.length - 1) : 0;
  const go = (delta) => setIndex((safe + delta + images.length) % images.length);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") { e.preventDefault(); go(1); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); go(-1); }
      else if (e.key === "Escape") { onClose(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, safe, images.length]);
  if (!open) return null;
  const prev = (e) => { e.stopPropagation(); go(-1); };
  const next = (e) => { e.stopPropagation(); go(1); };
  return (
    <div data-testid="lightbox" onClick={onClose} className="fixed inset-0 z-[120] flex items-center justify-center bg-black/90 p-4">
      <button data-testid="lightbox-close" onClick={onClose} className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20" aria-label="Close"><X className="h-6 w-6" /></button>
      {images.length > 1 && (
        <button data-testid="lightbox-prev" onClick={prev} className="absolute left-4 rounded-full bg-white/10 p-3 text-white hover:bg-white/20" aria-label="Previous"><ChevronLeft className="h-6 w-6" /></button>
      )}
      <img src={images[safe]} alt="" onClick={(e) => e.stopPropagation()} className="max-h-[85vh] max-w-[90vw] rounded-xl object-contain" data-testid="lightbox-image" />
      {images.length > 1 && (
        <button data-testid="lightbox-next" onClick={next} className="absolute right-4 rounded-full bg-white/10 p-3 text-white hover:bg-white/20" aria-label="Next"><ChevronRight className="h-6 w-6" /></button>
      )}
      <span className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-3 py-1 text-xs text-white">{safe + 1} / {images.length}</span>
    </div>
  );
}

function MaxChat({ listing, autoOpen = false, onBuy }) {
  const navigate = useNavigate();
  const [neg, setNeg] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [dealTcs, setDealTcs] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(true);
  const [isDesktop, setIsDesktop] = useState(typeof window !== "undefined" ? window.innerWidth >= 1024 : true);
  const [vh, setVh] = useState("100dvh");
  const inputRef = useRef(null);
  const deskMsgRef = useRef(null);
  const [deskMsgMax, setDeskMsgMax] = useState(260);

  const start = async () => {
    try {
      const { data } = await api.post("/negotiations/start", { listing_id: listing.id });
      setNeg(data); setMobileOpen(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start"); }
  };

  useEffect(() => { if (autoOpen) start(); /* eslint-disable-next-line */ }, [autoOpen]);
  useEffect(() => { document.querySelectorAll("[data-maxchat-scroll]").forEach((el) => { el.scrollTop = el.scrollHeight; }); }, [neg, sending, mobileOpen, isDesktop]);
  useEffect(() => {
    if (!isDesktop && mobileOpen && neg) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [isDesktop, mobileOpen, neg]);
  useEffect(() => {
    const v = window.visualViewport;
    const m = () => { setIsDesktop(window.innerWidth >= 1024); setVh(`${Math.max(360, Math.round(v ? v.height : window.innerHeight))}px`); };
    m(); window.addEventListener("resize", m); v?.addEventListener("resize", m); v?.addEventListener("scroll", m);
    return () => { window.removeEventListener("resize", m); v?.removeEventListener("resize", m); v?.removeEventListener("scroll", m); };
  }, []);

  // Desktop: size the message area so the panel grows with the chat yet always keeps the docked input on screen.
  useEffect(() => {
    if (!isDesktop) return;
    const calc = () => {
      const el = deskMsgRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      const avail = window.innerHeight - top - 160; // reserve space for the docked input/chips + breathing room
      setDeskMsgMax(Math.max(120, Math.min(360, Math.round(avail))));
    };
    calc();
    const t = setTimeout(calc, 250);
    window.addEventListener("resize", calc); window.addEventListener("scroll", calc, { passive: true });
    return () => { clearTimeout(t); window.removeEventListener("resize", calc); window.removeEventListener("scroll", calc); };
  }, [isDesktop, neg, sending]);

  const sendMsg = async (text) => {
    if (!text.trim() || sending) return;
    setNeg((p) => ({ ...p, messages: [...p.messages, { role: "user", content: text }] }));
    setSending(true);
    try {
      const { data } = await api.post(`/negotiations/${neg.id}/message`, { message: text });
      setNeg(data);
      if (data.status === "agreed") toast.success(`Deal agreed at ${AED(data.agreed_price)}!`);
    } catch { toast.error("Max is unavailable"); } finally { setSending(false); }
  };
  const send = () => { const m = input; setInput(""); sendMsg(m); };

  if (!neg) return (
    <div className="mt-3">
      <Button data-testid="make-offer-btn" variant="outline" onClick={start} className="w-full rounded-full border-primary text-primary hover:bg-primary/5">
        Make an Offer — chat with Max
      </Button>
      <p data-testid="offer-explainer" className="mt-1.5 text-xs text-slate-500">Max negotiates on the seller's behalf.</p>
    </div>
  );

  const header = (
    <div className="flex shrink-0 items-center gap-2 bg-primary px-4 py-3 text-white">
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 font-heading font-bold">M</div>
      <div><p className="text-sm font-semibold leading-none">Max</p><p className="text-[11px] text-white/70">Negotiation agent · online</p></div>
    </div>
  );

  const messageItems = (
    <>
      {neg.messages.map((m, i) => (
        <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
          <div className={`fade-up max-w-[80%] whitespace-pre-line rounded-2xl px-3 py-2 text-sm ${m.role === "user" ? "rounded-tr-none bg-primary text-white" : "rounded-tl-none bg-white text-slate-800 shadow-sm"}`}>
            <RichText text={m.content} />
          </div>
        </div>
      ))}
      {sending && (
        <div className="flex justify-start"><div className="flex gap-1 rounded-2xl rounded-tl-none bg-white px-3 py-3 shadow-sm">
          <span className="typing-dot h-2 w-2 rounded-full bg-slate-400" /><span className="typing-dot h-2 w-2 rounded-full bg-slate-400" /><span className="typing-dot h-2 w-2 rounded-full bg-slate-400" />
        </div></div>
      )}
    </>
  );

  const actions = (neg.status === "agreed" ? (
    <div className="border-t bg-emerald-50 p-3">
      <p className="mb-2 text-center text-sm font-semibold text-emerald-800">Deal agreed at {AED(neg.agreed_price)}!</p>
      <Button data-testid="proceed-checkout-btn" onClick={() => onBuy(neg.id, neg.agreed_price)} className="w-full rounded-full bg-primary font-semibold">Proceed to checkout</Button>
    </div>
  ) : (
    <>
      {neg.max_counter ? (
        <div className="flex gap-2 border-t bg-white px-3 pt-3" data-testid="max-chips">
          <Button data-testid="deal-chip" onClick={() => sendMsg(`Deal at AED ${neg.max_counter}`)} className="flex-1 rounded-full bg-primary text-sm font-semibold text-white hover:bg-primary/90">Deal — {AED(neg.max_counter)}</Button>
          <Button data-testid="another-offer-chip" variant="outline" onClick={() => inputRef.current?.focus()} className="flex-1 rounded-full text-sm">Make another offer</Button>
        </div>
      ) : null}
      <div className="flex items-center gap-2 border-t bg-white p-3">
        <Input ref={inputRef} data-testid="max-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Make an offer or ask a question…" className="rounded-full" />
        <Button data-testid="max-send" onClick={send} size="icon" className="shrink-0 rounded-full bg-primary" aria-label="Send"><Send className="h-4 w-4" /></Button>
      </div>
    </>
  ));

  // Desktop: opens compact (greeting + input visible, no page scroll), grows with the conversation up to
  // a cap, then scrolls internally with the input docked and always on screen.
  if (isDesktop) {
    return (
      <div className="mt-4 flex flex-col overflow-hidden rounded-2xl border border-slate-200" data-testid="max-chat">
        {header}
        <div ref={deskMsgRef} data-maxchat-scroll data-testid="max-messages" className="min-h-0 space-y-2 overflow-y-auto bg-slate-50 p-4" style={{ maxHeight: deskMsgMax, overscrollBehavior: "contain" }}>{messageItems}</div>
        <div className="shrink-0">{actions}</div>
      </div>
    );
  }

  // Mobile: full-screen panel with pinned item header, scrolling messages, docked actions; keyboard-safe height.
  return (
    <>
      {!mobileOpen && (
        <div className="mt-3">
          <Button data-testid="max-chat-reopen" variant="outline" onClick={() => setMobileOpen(true)} className="w-full rounded-full border-primary text-primary hover:bg-primary/5">
            Continue negotiation with Max
          </Button>
        </div>
      )}
      {mobileOpen && (
        <div className="fixed inset-x-0 top-0 z-[60] flex flex-col bg-white" style={{ height: vh }} data-testid="max-chat-mobile">
          <div className="flex shrink-0 items-center justify-between gap-2 border-b border-slate-200 bg-white px-4 py-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-slate-900">{listing.title}</p>
              <p className="text-xs font-bold text-primary">{AED(listing.listing_price)}</p>
            </div>
            <button type="button" data-testid="max-chat-close" onClick={() => setMobileOpen(false)} className="shrink-0 rounded-full p-1 text-slate-500 hover:bg-slate-100"><X className="h-5 w-5" /></button>
          </div>
          {header}
          <div data-maxchat-scroll data-testid="max-messages" className="min-h-0 flex-1 space-y-2 overflow-y-auto bg-slate-50 p-4" style={{ overscrollBehavior: "contain" }}>{messageItems}</div>
          <div className="max-h-[60%] shrink-0 overflow-y-auto" style={{ overscrollBehavior: "contain" }}>{actions}</div>
        </div>
      )}
    </>
  );
}

function FreeClaim({ listing, user, navigate, coverSrc }) {
  const [agree, setAgree] = useState(false);
  const [claimModal, setClaimModal] = useState(false);
  const feeConfig = useDeliveryFees();
  const fee = deliveryFeeForBox(listing.preferred_box_size, feeConfig);
  const TERMS = `Free items can’t be returned, and no concern can be raised after delivery. If the order is cancelled before delivery, your ${AED(fee)} is refunded. Your item will be left at your door.`;
  return (
    <div className="mt-5 rounded-2xl border border-slate-200 p-4" data-testid="free-claim-screen">
      <div className="flex items-center gap-3">
        {coverSrc && <img src={coverSrc} alt={listing.title} className="h-16 w-16 rounded-xl object-cover" />}
        <p className="font-semibold text-slate-900">{listing.title}</p>
      </div>
      <div className="mt-3 rounded-xl bg-slate-50 p-3 text-sm text-slate-700">
        <div className="flex justify-between"><span>Delivery fee</span><span>{AED(fee)}</span></div>
        <div className="mt-1 flex justify-between border-t border-slate-200 pt-1 font-semibold"><span>Total you pay</span><span>{AED(fee)}</span></div>
      </div>
      <p data-testid="free-terms-text" className="mt-3 text-xs text-slate-500">{TERMS}</p>
      <TcsAgreement checked={agree} onChange={setAgree} testid="free-claim-agree" className="mt-3" />
      <Button data-testid="free-claim-btn" disabled={!agree} onClick={() => user ? setClaimModal(true) : navigate("/login")} className="mt-3 w-full rounded-full font-semibold text-white hover:opacity-90" style={{ backgroundColor: "#3B63E0", opacity: agree ? 1 : 0.5 }}>Claim this item</Button>
      <PaymentTrust className="mt-2" testid="free-payment-trust" />
      <ConfirmPurchaseModal open={claimModal} onOpenChange={setClaimModal} listing={listing} user={user} feeConfig={feeConfig} isFree />
    </div>
  );
}
